import { useCallback, useEffect, useState } from 'react'
import { loadPayables, remainingCents, type Payable } from '@core/payments'
import { loadLetters, type LetterTask } from '@core/letters'
import { addDays, isoLocal } from '@core/calendar'
import { loadAppointments, type Appointment } from '@core/appointments'
import type { FamilySession } from '@core/session'
import { AppointmentForm } from '@features/calendar/AppointmentForm'
import { formatCents } from '@core/money'
import { Card, Screen, Section, Segmented, Spinner } from '@design/components'
import type { Space } from '@core/session'
import { Tags } from '@design/Tags'
import { PaymentCard } from './PaymentCard'
import { RunScreen } from './RunScreen'
import { UpcomingView } from './UpcomingView'
import { CalendarView } from './CalendarView'
import { AgendaView } from './AgendaView'
import { FilterSheet } from './FilterSheet'
import { applyFilters, countActive, isFiltering, NO_FILTERS, type Filters } from '@core/filters'

/**
 * Payments — three views of one set of obligations.
 *
 * spec.md §18: Calendar is a segment here rather than a tab of its own,
 * because it is the same data seen differently and giving it a tab would split
 * the mental model of "what I owe". Planner is deliberately not built yet
 * (§1591 defers it past V1); the running totals in Upcoming carry that load.
 *
 * The space filter is persistent — an admin working through the restaurant's
 * invoices should not have to reselect it every time they come back.
 */
/**
 * Four readings of the same obligations.
 *
 * "Agenda" and "Kalender" look like duplication and are not. The grid answers
 * *which days are heavy* — a shape you read at a glance and cannot get from a
 * list. The agenda answers *what is next*, which the grid genuinely cannot: on
 * the 28th it shows three days and then a wall.
 */
const SEGMENTS = [
  // Short labels, because four of them share the width of a phone. "Openstaand"
  // alone is nearly a quarter of a 375px screen at this size.
  { id: 'upcoming', label: 'Open' },
  { id: 'agenda', label: 'Agenda' },
  { id: 'calendar', label: 'Kalender' },
  { id: 'paid', label: 'Betaald' },
]

const FILTER_KEY = 'payments.space'

export function PaymentsTab({
  userId,
  spaces,
  session,
}: {
  userId: string
  spaces: Space[]
  session: FamilySession
}) {
  const [items, setItems] = useState<Payable[] | null>(null)
  const [tasks, setTasks] = useState<LetterTask[]>([])
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [makingAppointment, setMakingAppointment] = useState(false)
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null)
  const [segment, setSegment] = useState('upcoming')
  const [spaceId, setSpaceId] = useState<string>(() => readFilter())
  const [open, setOpen] = useState<Payable | null>(null)
  const [runningIn, setRunningIn] = useState<string | null>(null)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [filtering, setFiltering] = useState(false)

  const refresh = useCallback(() => {
    loadPayables()
      .then(setItems)
      .catch(() => setItems([]))
    // Deadlines share the calendar with payments, so they are loaded here too.
    // A quiet failure is right: a missing deadline dot must never stop the
    // payments themselves from rendering.
    // Both kinds here: a deadline is a deadline, whether it came from a letter
    // or from someone typing "keuring van de wagen".
    loadLetters('all')
      .then(setTasks)
      .catch(() => setTasks([]))
    // A window rather than everything: a year of appointments is not something
    // anyone scrolls, and the two views only ever look a couple of months out.
    const today = isoLocal(new Date())
    loadAppointments(addDays(today, -60), addDays(today, 400))
      .then(setAppointments)
      .catch(() => setAppointments([]))
  }, [])

  useEffect(refresh, [refresh])

  function chooseSpace(id: string) {
    setSpaceId(id)
    try {
      localStorage.setItem(FILTER_KEY, id)
    } catch {
      // Private mode, or storage full. The filter simply does not persist.
    }
  }

  if (!items) return <Spinner label="Even geduld…" />

  const inSpace = spaceId === 'all' ? items : items.filter((i) => i.spaceId === spaceId)
  const visible = applyFilters(inSpace, filters)
  const visibleTasks = spaceId === 'all' ? tasks : tasks.filter((t) => t.spaceId === spaceId)
  const visibleAppointments =
    spaceId === 'all' ? appointments : appointments.filter((a) => a.spaceId === spaceId)
  const activeSpaces = spaceId === 'all' ? spaces : spaces.filter((s) => s.id === spaceId)

  return (
    <>
      <div className="shrink-0 px-4 pt-1 pb-2 space-y-2">
        <Segmented options={SEGMENTS} active={segment} onSelect={setSegment} />
        <div className="flex items-center gap-2">
          {spaces.length > 1 && (
            <Segmented
              className="flex-1"
              options={[{ id: 'all', label: 'Alles' }, ...spaces.map((s) => ({ id: s.id, label: s.name }))]}
              active={spaceId}
              onSelect={chooseSpace}
            />
          )}
          <button
            onClick={() => setFiltering(true)}
            aria-label="Filteren"
            className={`shrink-0 min-h-[2.75rem] px-3 rounded-xl border text-sm font-medium ${
              isFiltering(filters) ? 'border-ink bg-ink/5' : 'border-line text-muted'
            }`}
          >
            Filter{isFiltering(filters) ? ` · ${countActive(filters)}` : ''}
          </button>
        </div>
      </div>

      {/* The calendar draws to its own edges, so it gets no side padding. */}
      {segment === 'calendar' ? (
        <Screen className="px-0">
          <CalendarView
            payables={visible}
            tasks={visibleTasks}
            appointments={visibleAppointments}
            userId={userId}
            onChanged={refresh}
            onNewAppointment={() => setMakingAppointment(true)}
            onOpenAppointment={setEditingAppointment}
          />
        </Screen>
      ) : (
        <Screen>
          {segment === 'upcoming' && (
            <UpcomingView
              items={visible}
              spaces={activeSpaces}
              onOpen={setOpen}
              onStartRun={setRunningIn}
            />
          )}
          {segment === 'agenda' && (
            <AgendaView
              payables={visible}
              tasks={visibleTasks}
              appointments={visibleAppointments}
              userId={userId}
              onChanged={refresh}
              onNewAppointment={() => setMakingAppointment(true)}
              onOpenAppointment={setEditingAppointment}
            />
          )}
          {segment === 'paid' && <PaidList items={visible} onOpen={setOpen} />}
        </Screen>
      )}

      {filtering && (
        <FilterSheet
          initial={filters}
          onApply={(next) => {
            setFilters(next)
            setFiltering(false)
          }}
          onClose={() => setFiltering(false)}
        />
      )}

      {open && (
        <PaymentCard
          payable={open}
          userId={userId}
          onClose={() => setOpen(null)}
          onPaid={() => {
            setOpen(null)
            refresh()
          }}
        />
      )}

      {(makingAppointment || editingAppointment) && (
        <AppointmentForm
          session={session}
          initial={editingAppointment}
          onDone={() => {
            setMakingAppointment(false)
            setEditingAppointment(null)
            refresh()
          }}
          onCancel={() => {
            setMakingAppointment(false)
            setEditingAppointment(null)
          }}
        />
      )}

      {runningIn && (
        <RunScreen
          spaceId={runningIn}
          userId={userId}
          onClose={() => {
            setRunningIn(null)
            refresh()
          }}
        />
      )}
    </>
  )
}

function PaidList({ items, onOpen }: { items: Payable[]; onOpen: (p: Payable) => void }) {
  const paid = items.filter((i) => i.state === 'paid').slice(0, 50)

  if (paid.length === 0) {
    return (
      <Card className="text-center py-8">
        <p className="font-medium mb-1">Nog niets betaald</p>
        <p className="text-muted text-sm">Wat je betaalt komt hier te staan.</p>
      </Card>
    )
  }

  return (
    <Section title="Betaald" count={paid.length}>
      <div className="space-y-2">
        {paid.map((item) => (
          <button key={item.instalmentId} onClick={() => onOpen(item)} className="w-full text-left">
            <Card className="flex items-center gap-3 opacity-70">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.supplierName ?? item.description}</p>
                <Tags
                  spaceName={item.spaceName}
                  spaceKind={item.spaceKind}
                  category={item.category}
                />
              </div>
              <span className="shrink-0 tabular-nums font-medium">
                {formatCents(item.amountCents - remainingCents(item))}
              </span>
            </Card>
          </button>
        ))}
      </div>
    </Section>
  )
}

function readFilter(): string {
  try {
    return localStorage.getItem(FILTER_KEY) ?? 'all'
  } catch {
    return 'all'
  }
}
